import React, { createContext, useState, useMemo, useCallback, useEffect, useLayoutEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { ThemeProvider as UnicityThemeProvider, useTheme as useDSTheme } from '@unicity/design-system';
import { useMixPanel } from './useMixPanel';

export const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  const { logEvent } = useMixPanel();

  const [mode, setMode] = useState(() => {    
    return localStorage.getItem('theme-mode') || 'light';
  });

  const [modeSystem, setModeSystem] = useState(() => {    
    return localStorage.getItem('theme-mode-system') === 'true' || false;
  });

  const [isApplyingTheme, setIsApplyingTheme] = useState(false);

  const setThemeMode = useCallback((mode) => {
    setIsApplyingTheme(true);
    setMode(mode);
    const payload = {
      version: process.env.REACT_APP_VERSION,
      location: 'set_theme_mode',
      mode: mode,
      system: modeSystem
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => logEvent('theme_monitor_location', payload));
    } else {
      setTimeout(() => logEvent('theme_monitor_location', payload), 0);
    }
  }, [ logEvent, modeSystem ]);

  const setThemeModeSystem = useCallback((modeSystem) => {
    setIsApplyingTheme(true);
    setModeSystem(modeSystem);
    const payload = {
      version: process.env.REACT_APP_VERSION,
      location: 'set_theme_mode_system',
      mode: mode === 'light' ? 'dark' : 'light',
      system: modeSystem
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => logEvent('theme_monitor_location', payload));
    } else {
      setTimeout(() => logEvent('theme_monitor_location', payload), 0);
    }
  }, [ logEvent, mode ]);

  const toggleTheme = useCallback(() => {
    const nextMode = mode === 'light' ? 'dark' : 'light';
    setIsApplyingTheme(true);
    setMode(nextMode);
    setModeSystem(false);
    const payload = {
      version: process.env.REACT_APP_VERSION,
      location: 'toggle_theme',
      mode: nextMode,
      system: modeSystem
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => logEvent('theme_monitor_location', payload));
    } else {
      setTimeout(() => logEvent('theme_monitor_location', payload), 0);
    }
  }, [ mode, logEvent, modeSystem ]);

  const effectiveMode = useMemo(() => {
    if (modeSystem) {
      const prefersDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
      return prefersDarkMode ? 'dark' : 'light';
    }
    return mode;
  }, [mode, modeSystem]);

  // Apply side effects outside render to keep toggling snappy
  useLayoutEffect(() => {
    const currentTheme = document.documentElement.getAttribute('data-theme');
    if (currentTheme !== effectiveMode) {
      document.documentElement.setAttribute('data-theme', effectiveMode);
    }
  }, [effectiveMode]);

  // Hide the overlay as soon as MUI theme context has updated and next paint occurs
  useEffect(() => {
    if (!isApplyingTheme) return;
    const id = requestAnimationFrame(() => setIsApplyingTheme(false));
    return () => cancelAnimationFrame(id);
  }, [isApplyingTheme, effectiveMode]);

  // Persist settings without blocking paint
  useEffect(() => {
    const storedMode = localStorage.getItem('theme-mode');
    if (storedMode !== effectiveMode) {
      try { localStorage.setItem('theme-mode', effectiveMode); } catch { /* ignore storage errors */ }
    }

    const storedModeSystem = localStorage.getItem('theme-mode-system');
    if (storedModeSystem !== String(modeSystem)) {
      try { localStorage.setItem('theme-mode-system', String(modeSystem)); } catch { /* ignore storage errors */ }
    }
  }, [effectiveMode, modeSystem]);

  // Analytics logging is deferred and decoupled from paint
  useEffect(() => {
    const payload = {
      version: process.env.REACT_APP_VERSION,
      location: 'theme_monitor',
      mode: effectiveMode,
      system: modeSystem
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => logEvent('theme_monitor', payload));
    } else {
      setTimeout(() => logEvent('theme_monitor', payload), 0);
    }
  }, [effectiveMode, modeSystem, logEvent]);

  // React to OS theme changes when system mode is enabled
  useEffect(() => {
    if (!modeSystem) return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      setMode(media.matches ? 'dark' : 'light');
    };
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, [modeSystem]);

  const themeContextValue = useMemo(() => ({
    mode,
    modeSystem,
    toggleTheme,
    setThemeMode,
    setThemeModeSystem
  }), [mode, modeSystem, toggleTheme,setThemeMode, setThemeModeSystem]);

  const DSThemeWatcher = () => {
    const dsTheme = useDSTheme();
    useEffect(() => {
      const dsResolved = dsTheme?.resolvedMode;
      if (!dsResolved) return;
      if (dsResolved !== mode) {
        setIsApplyingTheme(true);
        setModeSystem(false);
        setMode(dsResolved);
      }
       
    }, [dsTheme?.resolvedMode]);
    return null;
  };

  return (
    <ThemeContext.Provider value={themeContextValue}>
        <UnicityThemeProvider mode={effectiveMode}>
          <DSThemeWatcher />
          {children}
          {isApplyingTheme && (
            <Box
              data-test-id="theme-apply-overlay"
              role="status"
              aria-live="polite"
              sx={{
                position: 'fixed',
                inset: 0,
                zIndex: 20000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0,0,0,0)',
                pointerEvents: 'auto',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <CircularProgress color="primary" size={28} thickness={5} />
                <Typography variant="body2">Theme is being applied…</Typography>
              </Box>
            </Box>
          )}
        </UnicityThemeProvider>      
    </ThemeContext.Provider>
  );
}; 